import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { db } from "../../firebase";
import { doc, deleteDoc, updateDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { validateCustomerForm } from "../../utils/validations";
import { setLoading } from "../../redux/loaderSlice";
import { useDispatch } from "react-redux";
import { Eye, Pencil, Trash2, FileText, Download } from "lucide-react";


const MAX_FILE_BYTES = 819200; // 800 KB
// Container
const Wrapper = styled.div`
  margin-top: 20px;
`;

// Desktop Table Container
const TableContainer = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Table
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  font-size: 13px;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  border-bottom: 1px solid #eee;
`;

// Mobile Card View
const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  padding: 14px;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 13px;
`;

const Label = styled.span`
  color: #6b7280;
`;

const Value = styled.span`
  font-weight: 600;
`;

const DocLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 768px) {
    align-items: flex-end;
  }
`;

const DocActionBtn = styled.button`
  font-size: 11px;
  color: #1d4ed8;
  text-decoration: none;
  padding: 3px 6px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #dbeafe;
  }
`;

/** Large data: URLs often open as a blank tab until refresh; blob: URLs preview reliably. */
async function dataUrlToBlob(dataUrl) {
  const res = await fetch(dataUrl);
  return res.blob();
}

async function openDataUrlInNewTab(dataUrl) {
  let objectUrl;
  try {
    const blob = await dataUrlToBlob(dataUrl);
    objectUrl = URL.createObjectURL(blob);
    const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      URL.revokeObjectURL(objectUrl);
      toast.error("Popup blocked — allow popups to preview");
      return;
    }
    // Do not revoke on a timer: refreshing the preview tab reloads the same blob: URL and
    // would show ERR_FILE_NOT_FOUND after revoke. Revoke only when the preview tab closes.
    const poll = setInterval(() => {
      if (win.closed) {
        clearInterval(poll);
        URL.revokeObjectURL(objectUrl);
      }
    }, 1500);
  } catch (err) {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    console.error("openDataUrlInNewTab:", err);
    toast.error("Could not open preview");
  }
}

async function downloadDataUrl(dataUrl, filename) {
  let objectUrl;
  try {
    const blob = await dataUrlToBlob(dataUrl);
    objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    console.error("downloadDataUrl:", err);
    toast.error("Could not download file");
  }
}

// Actions
const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const Btn = styled.button`
  flex: 1;
  font-size: 14px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  transition: all 0.2s;
  
  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #f3f4f6;
  }

  ${({ variant }) =>
    variant === "view" &&
    ` color: #1D4ED8;`}

  ${({ variant }) =>
    variant === "edit" &&
    ` color: #92400E;`}

  ${({ variant }) =>
    variant === "delete" &&
    ` color: #991B1B;`}
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 20px;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  ${({ fullWidth }) => fullWidth && `grid-column: 1 / -1;`}
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  font-weight: 500;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #1f2937;
  word-break: break-all;
  background: #f9fafb;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const RenderInlineDoc = ({ docUrl, fileName }) => {
  if (!docUrl) return <DetailValue>Not uploaded</DetailValue>;

  const isPdf = docUrl.startsWith("data:application/pdf") || (fileName && fileName.toLowerCase().endsWith(".pdf"));

  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
      {isPdf ? (
        <iframe src={docUrl} title={fileName || "PDF Document"} style={{ width: "100%", height: "300px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" }} />
      ) : (
        <img src={docUrl} alt={fileName || "Document"} style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain", borderRadius: "8px", border: "1px solid #e5e7eb", background: "#f9fafb" }} />
      )}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
        <DocActionBtn type="button" onClick={() => openDataUrlInNewTab(docUrl)}>Open Full Screen</DocActionBtn>
        <DocActionBtn type="button" onClick={() => downloadDataUrl(docUrl, fileName || "document")}>Download</DocActionBtn>
      </div>
    </div>
  );
};

export default function AllCustomers({ data = [], refresh }) {
  const dispatch = useDispatch();
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [viewCustomer, setViewCustomer] = useState(null);

  const editableFields = useMemo(
    () => [
      { key: "customerName", label: "Customer Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address" },
      { key: "bankName", label: "Bank Name" },
      { key: "branch", label: "Branch" },
      { key: "accountNumber", label: "Account Number" },
      { key: "ifsc", label: "IFSC" },
    ],
    []
  );

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  // ---------------- DELETE ----------------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;

    dispatch(setLoading(true));
    try {
      await deleteDoc(doc(db, "customers", id));
      toast.success("Customer deleted");
      refresh?.();
    } catch (err) {
      toast.error("Failed to delete customer");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  // ---------------- EDIT INIT ----------------
  const handleEdit = (item) => {
    setEditId(item.id);
    setEditData(item);
    setEditErrors({});
  };

  // ---------------- SAVE EDIT ----------------
  const handleSave = async () => {
    const payload = {
      ...editData,
      phone: String(editData.phone ?? "").replace(/\D/g, "").slice(0, 10),
      accountNumber: String(editData.accountNumber ?? "").replace(/\D/g, ""),
      ifsc: String(editData.ifsc ?? "").toUpperCase(),
    };

    const validation = validateCustomerForm(payload);
    if (!validation.isValid) {
      setEditErrors(validation.errors);
      toast.error("Please fix validation errors");
      return;
    }

    dispatch(setLoading(true));
    try {
      await updateDoc(doc(db, "customers", editId), payload);
      setEditId(null);
      setEditData({});
      setEditErrors({});
      toast.success("Customer updated");
      refresh?.();
    } catch (err) {
      toast.error("Failed to update customer");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  // ---------------- INPUT CHANGE ----------------
  const handleChange = (e, field) => {
    let value = e.target.value;
    if (field === "phone") value = value.replace(/\D/g, "").slice(0, 10);
    if (field === "accountNumber") value = value.replace(/\D/g, "");
    if (field === "ifsc") value = value.toUpperCase();

    setEditData((prev) => ({
      ...prev,
      [field]: value
    }));

    setEditErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleFileChange = async (event, fieldName, fileNameField) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File too large. Max 800 KB allowed.");
      try { event.target.value = ""; } catch (e) { }
      return;
    }
    try {
      const encoded = await toBase64(file);
      setEditData((prev) => ({
        ...prev,
        [fieldName]: encoded,
        [fileNameField]: file.name,
      }));
      setEditErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    } catch (error) {
      toast.error("Failed to read file");
    }
  };

  const renderDocActions = (docValue, fileName, label) => {
    if (!docValue) return <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 4 }}>{label}: N/A</div>;
    const resolvedName = fileName || `${label}.file`;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>
        <span style={{ fontWeight: 600, minWidth: "55px" }}>{label}:</span>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button 
            type="button" 
            onClick={() => openDataUrlInNewTab(docValue)} 
            title={`${label} Preview`}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", padding: 2, cursor: "pointer", color: "#3b82f6" }}
          >
            <FileText size={16} />
          </button>
          <button 
            type="button" 
            onClick={() => downloadDataUrl(docValue, resolvedName)} 
            title={`${label} Download`}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", padding: 2, cursor: "pointer", color: "#10b981" }}
          >
            <Download size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <Wrapper>

      {/* ================= DESKTOP TABLE ================= */}
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Customer ID</Th>
              <Th>Name</Th>
              <Th>Phone</Th>
              <Th>Email</Th>
              <Th>Documents</Th>
              <Th>Actions</Th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <Td colSpan="6" style={{ textAlign: "center" }}>
                  No data available
                </Td>
              </tr>
            ) : (
              data.map((item, i) => (
                <tr key={i}>
                  <Td>{item.customerId || "-"}</Td>

                  <Td>
                    {editId === item.id ? (
                      <>
                        <input value={editData.customerName || ""} onChange={(e) => handleChange(e, "customerName")} />
                        {editErrors.customerName && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.customerName}</div>}
                      </>
                    ) : (
                      item.customerName
                    )}
                  </Td>

                  <Td>
                    {editId === item.id ? (
                      <>
                        <input value={editData.phone || ""} onChange={(e) => handleChange(e, "phone")} />
                        {editErrors.phone && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.phone}</div>}
                      </>
                    ) : (
                      item.phone
                    )}
                  </Td>

                  <Td>
                    {editId === item.id ? (
                      <>
                        <input value={editData.email || ""} onChange={(e) => handleChange(e, "email")} />
                        {editErrors.email && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.email}</div>}
                      </>
                    ) : (
                      item.email || "-"
                    )}
                  </Td>

                  <Td>
                    <DocLinks>
                      {renderDocActions(item.aadharCard, item.aadharCardName, "Aadhar")}
                      {renderDocActions(item.panCard, item.panCardName, "PAN")}
                    </DocLinks>
                  </Td>

                  <Td>
                    <Actions>
                      <Btn
                        variant="view"
                        onClick={() => setViewCustomer(item)}
                      >
                        <Eye /> 
                      </Btn>

                      {/* EDIT */}
                      {editId === item.id ? (
                        <>
                          <Btn variant="edit" onClick={handleSave}>
                            Update
                          </Btn>
                          <Btn variant="view" onClick={() => {
                            setEditId(null);
                            setEditData({});
                            setEditErrors({});
                          }}>
                            Cancel
                          </Btn>
                        </>
                      ) : (
                        <Btn
                          variant="edit"
                          onClick={() => handleEdit(item)}
                        >
                          <Pencil /> 
                        </Btn>
                      )}

                      {/* DELETE */}
                      <Btn
                        variant="delete"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 /> 
                      </Btn>
                    </Actions>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>

      {editId && (
        <ModalOverlay onClick={() => { setEditId(null); setEditData({}); setEditErrors({}); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Customer Details</ModalTitle>
              <CloseButton onClick={() => { setEditId(null); setEditData({}); setEditErrors({}); }}>&times;</CloseButton>
            </ModalHeader>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
              {editableFields.map((field) => (
                <div key={field.key}>
                  <label style={{ fontSize: 12, color: "#6b7280" }}>{field.label}</label>
                  <input
                    style={{ width: "100%", padding: 8, borderRadius: 6, border: `1px solid ${editErrors[field.key] ? "#dc2626" : "#d1d5db"}` }}
                    value={editData[field.key] || ""}
                    onChange={(e) => handleChange(e, field.key)}
                  />
                  {editErrors[field.key] && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors[field.key]}</div>}
                </div>
              ))}

              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>Profile Picture</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleFileChange(event, "profilePicture", "profilePictureName")}
                />
                {editData.profilePictureName && <div style={{ fontSize: 11 }}>{editData.profilePictureName}</div>}
                {editErrors.profilePicture && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.profilePicture}</div>}
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>Aadhar Card</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => handleFileChange(event, "aadharCard", "aadharCardName")}
                />
                {editData.aadharCardName && <div style={{ fontSize: 11 }}>{editData.aadharCardName}</div>}
                {editErrors.aadharCard && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.aadharCard}</div>}
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>PAN Card</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => handleFileChange(event, "panCard", "panCardName")}
                />
                {editData.panCardName && <div style={{ fontSize: 11 }}>{editData.panCardName}</div>}
                {editErrors.panCard && <div style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.panCard}</div>}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button onClick={handleSave} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, background: "#fef3c7", border: "none", cursor: "pointer" }}>
                Update
              </button>
              <button onClick={() => { setEditId(null); setEditData({}); setEditErrors({}); }} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, background: "#e5e7eb", border: "none", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ================= MOBILE CARDS ================= */}
      <MobileContainer>
        {data.length === 0 ? (
          <Card>No customers found</Card>
        ) : (
          data.map((item, i) => (
            <Card key={i}>
              <Row>
                <Label>Customer ID</Label>
                <Value>{item.customerId || "-"}</Value>
              </Row>

              <Row>
                <Label>Name</Label>

                {editId === item.id ? (
                  <input
                    value={editData.customerName || ""}
                    onChange={(e) =>
                      handleChange(e, "customerName")
                    }
                  />
                ) : (
                  <Value>{item.customerName}</Value>
                )}
              </Row>

              <Row>
                <Label>Phone</Label>
                {editId === item.id ? (
                  <>
                    <input value={editData.phone || ""} onChange={(e) => handleChange(e, "phone")} />
                    {editErrors.phone && <span style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.phone}</span>}
                  </>
                ) : (
                  <Value>{item.phone}</Value>
                )}
              </Row>

              <Row>
                <Label>Email</Label>
                {editId === item.id ? (
                  <>
                    <input value={editData.email || ""} onChange={(e) => handleChange(e, "email")} />
                    {editErrors.email && <span style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.email}</span>}
                  </>
                ) : (
                  <Value>{item.email || "-"}</Value>
                )}
              </Row>

              <Row>
                <Label>Address</Label>
                {editId === item.id ? (
                  <>
                    <input value={editData.address || ""} onChange={(e) => handleChange(e, "address")} />
                    {editErrors.address && <span style={{ color: "#dc2626", fontSize: 11 }}>{editErrors.address}</span>}
                  </>
                ) : (
                  <Value>{item.address || "-"}</Value>
                )}
              </Row>

              <Row>
                <Label>Bank / IFSC</Label>
                <Value>{item.bankName || "-"} / {item.ifsc || "-"}</Value>
              </Row>

              <Row>
                <Label>Documents</Label>
                <DocLinks>
                  {renderDocActions(item.aadharCard, item.aadharCardName, "Aadhar")}
                  {renderDocActions(item.panCard, item.panCardName, "PAN")}
                </DocLinks>
              </Row>

              <Actions>
                <Btn
                  variant="view"
                  onClick={() => setViewCustomer(item)}
                >
                  <Eye /> 
                </Btn>

                {editId === item.id ? (
                  <>
                    <Btn variant="edit" onClick={handleSave}>
                      Update
                    </Btn>
                    <Btn variant="view" onClick={() => {
                      setEditId(null);
                      setEditData({});
                      setEditErrors({});
                    }}>
                      Cancel
                    </Btn>
                  </>
                ) : (
                  <Btn
                    variant="edit"
                    onClick={() => handleEdit(item)}
                  >
                    <Pencil /> 
                  </Btn>
                )}

                <Btn
                  variant="delete"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 /> 
                </Btn>
              </Actions>
            </Card>
          ))
        )}
      </MobileContainer>

      {/* ================= VIEW MODAL ================= */}
      {viewCustomer && (
        <ModalOverlay onClick={() => setViewCustomer(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              {/* <ModalTitle>Customer Details  */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {viewCustomer.profilePictureName && <img src={viewCustomer.profilePicture} alt="Profile" style={{ width: 50, height: 50, borderRadius: 50 }} />} <span>{viewCustomer.customerName}</span>
              </div>
              {/* </ModalTitle> */}
              <CloseButton onClick={() => setViewCustomer(null)}>&times;</CloseButton>
            </ModalHeader>
            <DetailGrid>
              <DetailRow>
                <DetailLabel>Customer ID</DetailLabel>
                <DetailValue>{viewCustomer.customerId || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Customer Name</DetailLabel>
                <DetailValue>{viewCustomer.customerName || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{viewCustomer.phone || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{viewCustomer.email || "-"}</DetailValue>
              </DetailRow>
              <DetailRow fullWidth>
                <DetailLabel>Address</DetailLabel>
                <DetailValue>{viewCustomer.address || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Bank Name</DetailLabel>
                <DetailValue>{viewCustomer.bankName || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Branch</DetailLabel>
                <DetailValue>{viewCustomer.branch || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Account Number</DetailLabel>
                <DetailValue>{viewCustomer.accountNumber || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>IFSC</DetailLabel>
                <DetailValue>{viewCustomer.ifsc || "-"}</DetailValue>
              </DetailRow>
              <DetailRow fullWidth>
                <DetailLabel>Aadhar Card {viewCustomer.aadharCardName && `(${viewCustomer.aadharCardName})`}</DetailLabel>
                <RenderInlineDoc docUrl={viewCustomer.aadharCard} fileName={viewCustomer.aadharCardName} />
              </DetailRow>
              <DetailRow fullWidth>
                <DetailLabel>PAN Card {viewCustomer.panCardName && `(${viewCustomer.panCardName})`}</DetailLabel>
                <RenderInlineDoc docUrl={viewCustomer.panCard} fileName={viewCustomer.panCardName} />
              </DetailRow>
            </DetailGrid>
          </ModalContent>
        </ModalOverlay>
      )}
    </Wrapper>
  );
}